import React from "react";

// Tier metadata mirrors /api/reputation/tiers — Phase 1a gem evolution naming.
// `animClass` references CSS keyframes defined in index.css. Each tier has a
// distinct visual identity (color, glow, shape, animation) so users can spot
// rarity at a glance across feed, comments, messages, orders and discovery.
export const TIERS = {
  white:   { name: "White Gem",      label: "New User",            color: "#E5E7EB", aura: "rgba(229,231,235,0.35)", animClass: "",                  variant: "classic" },
  green:   { name: "Neon Gem",       label: "Active User",         color: "#39FF14", aura: "rgba(57,255,20,0.45)",   animClass: "gem-anim-neon",      variant: "classic" },
  blue:    { name: "Prism Gem",      label: "Trusted User",        color: "#60A5FA", aura: "rgba(96,165,250,0.50)",  animClass: "gem-anim-prism",     variant: "prism" },
  purple:  { name: "Ember Gem",      label: "Rising Star",         color: "#FF6B35", aura: "rgba(255,107,53,0.55)",  animClass: "gem-anim-ember",     variant: "ember" },
  gold:    { name: "Diamond Gem",    label: "Top Rated",           color: "#B5E2FF", aura: "rgba(181,226,255,0.65)", animClass: "gem-anim-diamond",   variant: "diamond" },
  ruby:    { name: "Black Opal Gem", label: "Elite Status",        color: "#7C3AED", aura: "rgba(124,58,237,0.65)",  animClass: "gem-anim-opal",      variant: "opal" },
  diamond: { name: "Legendary Gem",  label: "Hidden Gem Legend",   color: "#FFD700", aura: "rgba(255,215,0,0.75)",   animClass: "gem-anim-legendary", variant: "legendary" },
};

const SIZE = {
  xs: { w: 12, h: 12, stroke: 1 },
  sm: { w: 14, h: 14, stroke: 1 },
  md: { w: 18, h: 18, stroke: 1.25 },
  lg: { w: 28, h: 28, stroke: 1.5 },
  xl: { w: 56, h: 56, stroke: 2 },
};

/**
 * GemBadge — a polished SVG gem with tier-specific shape, glow, and animation.
 *
 * Props:
 *   tier: one of TIERS keys (white | green | blue | purple | gold | ruby | diamond)
 *   size: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
 *   showLabel: bool — render tier label inline as a pill
 *   title: optional override
 *   variant: optional equipped gem-variant cosmetic. Shape:
 *     {render: "tier"|"food", tier_alias, icon, color, anim_class, label}
 *     When present, the variant REPLACES the default tier visual everywhere
 *     this component is rendered.
 */
export default function GemBadge({ tier = "white", size = "sm", showLabel = false, title, variant }) {
  // Variant overrides — when a user equips a gem cosmetic, render the variant
  // instead of their computed tier. The score/tier label still reflects truth;
  // only the *visual* is the variant.
  let activeTier = tier;
  let isFoodVariant = false;
  let foodIcon = null;
  let foodColor = null;
  let foodAnim = null;
  let foodLabel = null;
  let isCustomVariant = false;
  let customImageUrl = null;
  let customAnimClass = null;
  let customSpeedMs = 1200;
  let customScalePct = 100;
  let customLoop = 0;
  let customEmoji = null;
  let customLabel = null;
  let customColor = null;

  if (variant) {
    if (variant.render === "custom" || variant.is_custom) {
      isCustomVariant = true;
      customImageUrl = variant.image_url || null;
      customAnimClass = variant.anim_class || variant.animation_class || "gem-anim-neon";
      customSpeedMs = Number(variant.animation_speed_ms) || 1200;
      customScalePct = Number(variant.animation_scale_pct) || 100;
      customLoop = Number(variant.loop_count) || 0;
      customEmoji = variant.emoji || "💎";
      customLabel = variant.label;
      customColor = variant.color || "#39FF14";
    } else if (variant.render === "tier" && variant.tier_alias && TIERS[variant.tier_alias]) {
      activeTier = variant.tier_alias;
    } else if (variant.render === "food") {
      isFoodVariant = true;
      foodIcon = variant.icon;
      foodColor = variant.color || "#FFD700";
      foodAnim = variant.anim_class || "gem-anim-neon";
      foodLabel = variant.label;
    }
  }

  const t = TIERS[activeTier] || TIERS.white;
  const dim = SIZE[size] || SIZE.sm;
  const tooltip = title || (variant?.label ? `${variant.label} · cosmetic` : `${t.name} · ${t.label}`);

  // ---- Phase E-2: DB-driven custom gem — render uploaded artwork or emoji ----
  if (isCustomVariant) {
    const src = customImageUrl
      ? (customImageUrl.startsWith("http") ? customImageUrl : `${process.env.REACT_APP_BACKEND_URL || ""}${customImageUrl}`)
      : null;
    const iter = customLoop === 0 ? "infinite" : customLoop;
    const animStyle = customImageUrl ? {} : {
      animationDuration: `${customSpeedMs}ms`,
      animationIterationCount: iter,
    };
    return (
      <span
        title={tooltip}
        className="inline-flex items-center gap-1 align-middle"
        data-testid={`gem-badge-custom-${variant?.id || "unknown"}`}
        data-gem-variant={variant?.id}
        data-gem-name={customLabel}
      >
        <span
          className={`inline-block ${src ? "" : customAnimClass}`}
          style={{
            width: dim.w,
            height: dim.h,
            transform: customScalePct !== 100 ? `scale(${customScalePct / 100})` : undefined,
            transformOrigin: "center",
            display: "inline-block",
            ...animStyle,
          }}
          data-gem-custom-anim={customAnimClass}
        >
          {src ? (
            <img
              src={src}
              alt={customLabel || "gem"}
              width={dim.w}
              height={dim.h}
              style={{
                width: dim.w,
                height: dim.h,
                objectFit: "contain",
                display: "block",
                animation: `${customAnimClass} ${customSpeedMs}ms linear ${iter}`,
              }}
              draggable={false}
              data-testid={`gem-badge-custom-img`}
            />
          ) : (
            <span
              style={{ fontSize: `${dim.w}px`, lineHeight: 1, display: "inline-block" }}
            >
              {customEmoji}
            </span>
          )}
        </span>
        {showLabel && customLabel && (
          <span
            className="text-[10px] uppercase tracking-widest font-black px-1.5 py-0.5 rounded-full border"
            style={{ color: customColor, borderColor: `${customColor}55`, background: `${customColor}14` }}
          >
            {customLabel}
          </span>
        )}
      </span>
    );
  }

  if (isFoodVariant) {
    return (
      <span
        title={tooltip}
        className="inline-flex items-center gap-1 align-middle"
        data-testid={`gem-badge-food-${foodIcon}`}
        data-gem-variant={variant?.id}
        data-gem-name={foodLabel}
      >
        <FoodGem icon={foodIcon} color={foodColor} animClass={foodAnim} dim={dim} />
        {showLabel && (
          <span
            className="text-[10px] uppercase tracking-widest font-black px-1.5 py-0.5 rounded-full border"
            style={{ color: foodColor, borderColor: `${foodColor}55`, background: `${foodColor}14` }}
          >
            {foodLabel}
          </span>
        )}
      </span>
    );
  }

  const gid = `${activeTier}-${size}-${Math.random().toString(36).slice(2, 7)}`;
  return (
    <span
      title={tooltip}
      className="inline-flex items-center gap-1 align-middle"
      data-testid={`gem-badge-${activeTier}`}
      data-gem-variant={variant?.id || null}
      data-gem-tier={activeTier}
      data-gem-name={variant?.label || t.name}
    >
      <svg
        width={dim.w}
        height={dim.h}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={variant?.anim_class || t.animClass}
      >
        <defs>
          <linearGradient id={`g-classic-${gid}`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%"   stopColor={t.color} stopOpacity="1" />
            <stop offset="60%"  stopColor={t.color} stopOpacity="0.85" />
            <stop offset="100%" stopColor="#0b0b0f" stopOpacity="0.9" />
          </linearGradient>
          <linearGradient id={`g-prism-${gid}`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%"   stopColor="#60A5FA" />
            <stop offset="35%"  stopColor="#A78BFA" />
            <stop offset="65%"  stopColor="#F472B6" />
            <stop offset="100%" stopColor="#22D3EE" />
          </linearGradient>
          <radialGradient id={`g-ember-${gid}`} cx="0.5" cy="0.6" r="0.7">
            <stop offset="0%"   stopColor="#FFE082" />
            <stop offset="40%"  stopColor="#FF8C42" />
            <stop offset="80%"  stopColor="#C53030" />
            <stop offset="100%" stopColor="#2A0A0A" />
          </radialGradient>
          <linearGradient id={`g-diamond-${gid}`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%"   stopColor="#FFFFFF" />
            <stop offset="50%"  stopColor="#E0F2FE" />
            <stop offset="100%" stopColor="#7DD3FC" />
          </linearGradient>
          <linearGradient id={`g-opal-${gid}`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%"   stopColor="#7C3AED" />
            <stop offset="40%"  stopColor="#0F172A" />
            <stop offset="70%"  stopColor="#22D3EE" stopOpacity="0.7" />
            <stop offset="100%" stopColor="#EC4899" stopOpacity="0.7" />
          </linearGradient>
          <linearGradient id={`g-legendary-${gid}`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%"   stopColor="#FFD700" />
            <stop offset="35%"  stopColor="#FF6B35" />
            <stop offset="65%"  stopColor="#EC4899" />
            <stop offset="100%" stopColor="#22D3EE" />
          </linearGradient>
        </defs>

        {t.variant === "legendary" && (
          <g className="gem-anim-legendary-spin" style={{ transformOrigin: "12px 12px" }}>
            <circle cx="12" cy="12" r="11" fill="none" stroke="#FFD700" strokeWidth="0.4" strokeDasharray="2 2" opacity="0.6" />
            <circle cx="12" cy="12" r="9.5" fill="none" stroke="#EC4899" strokeWidth="0.3" strokeDasharray="1.5 3" opacity="0.5" />
          </g>
        )}

        {renderGemShape(t.variant, gid, dim.stroke, t.color)}

        {t.variant !== "opal" && t.variant !== "legendary" && (
          <path d="M10.2 4.5l1.2 1.8-1.8 0.6 0.6-2.4Z" fill="rgba(255,255,255,0.85)" />
        )}
        {t.variant === "legendary" && (
          <>
            <circle cx="6"  cy="6"  r="0.5" fill="#FFFFFF" />
            <circle cx="18" cy="7"  r="0.4" fill="#FFFFFF" />
            <circle cx="17" cy="17" r="0.3" fill="#FFFFFF" />
            <path d="M10.2 4.5l1.2 1.8-1.8 0.6 0.6-2.4Z" fill="rgba(255,255,255,0.95)" />
          </>
        )}
      </svg>
      {showLabel && (
        <span
          className="text-[10px] uppercase tracking-widest font-black px-1.5 py-0.5 rounded-full border"
          style={{ color: t.color, borderColor: `${t.color}55`, background: `${t.color}14` }}
        >
          {t.label}
        </span>
      )}
    </span>
  );
}

/**
 * FoodGem — a custom food icon rendered inside a hexagonal gem-styled badge
 * with gradient ring, glow, and animation. Designed to look distinct from
 * plain emojis: each icon is a unique SVG illustration.
 */
function FoodGem({ icon, color, animClass, dim }) {
  const gid = `food-${icon}-${Math.random().toString(36).slice(2, 7)}`;
  return (
    <svg
      width={dim.w}
      height={dim.h}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={animClass}
    >
      <defs>
        <radialGradient id={`fg-bg-${gid}`} cx="0.5" cy="0.5" r="0.6">
          <stop offset="0%"   stopColor={color} stopOpacity="0.55" />
          <stop offset="70%"  stopColor={color} stopOpacity="0.18" />
          <stop offset="100%" stopColor="#0b0b0f" stopOpacity="0.95" />
        </radialGradient>
        <linearGradient id={`fg-ring-${gid}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%"   stopColor={color} />
          <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0.7" />
        </linearGradient>
      </defs>
      {/* Hexagonal gem-shaped container — distinct from circular avatars */}
      <path
        d="M12 1.8 L20.7 6.6 L20.7 17.4 L12 22.2 L3.3 17.4 L3.3 6.6 Z"
        fill={`url(#fg-bg-${gid})`}
        stroke={`url(#fg-ring-${gid})`}
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
      {/* Inner ring for premium feel */}
      <path
        d="M12 3.6 L18.9 7.5 L18.9 16.5 L12 20.4 L5.1 16.5 L5.1 7.5 Z"
        fill="none"
        stroke={color}
        strokeWidth="0.4"
        strokeOpacity="0.4"
      />
      {/* Sparkle highlight (top-left) */}
      <circle cx="7" cy="6.5" r="0.5" fill="rgba(255,255,255,0.9)" />
      {/* Food icon centered at (12,12) */}
      <g transform="translate(6.5,6.5) scale(0.46)">
        {renderFoodIcon(icon, color)}
      </g>
    </svg>
  );
}

/**
 * 12 custom food icon SVGs. Each is drawn inside a 24×24 box, centered, and
 * shaded with the variant's primary color. Designed to feel like Hidden Gem
 * branded illustrations rather than generic emoji.
 */
function renderFoodIcon(icon, color) {
  switch (icon) {
    case "burger":
      return (
        <>
          {/* Top bun */}
          <path d="M2 7 Q12 0 22 7 L22 9 L2 9 Z" fill="#F59E0B" stroke="#92400E" strokeWidth="0.6" />
          {/* Sesame seeds */}
          <circle cx="7"  cy="5.5" r="0.5" fill="#FEF3C7" />
          <circle cx="12" cy="4.2" r="0.5" fill="#FEF3C7" />
          <circle cx="17" cy="5.5" r="0.5" fill="#FEF3C7" />
          {/* Lettuce */}
          <path d="M2 9 Q5 11 8 9 Q11 11 14 9 Q17 11 20 9 Q22 11 22 11 L2 11 Z" fill="#22C55E" />
          {/* Patty */}
          <rect x="2" y="11" width="20" height="3.5" fill="#7C2D12" rx="0.5" />
          {/* Cheese */}
          <path d="M1.5 14.5 Q5 16 12 14.5 Q19 16 22.5 14.5 L22.5 16 L1.5 16 Z" fill="#FBBF24" />
          {/* Bottom bun */}
          <path d="M2 16 Q12 22 22 16 L22 18 Q12 20 2 18 Z" fill="#D97706" stroke="#7C2D12" strokeWidth="0.6" />
        </>
      );
    case "taco":
      return (
        <>
          {/* Shell */}
          <path d="M2 17 Q2 5 12 5 Q22 5 22 17 Q22 19 12 19 Q2 19 2 17 Z" fill="#FBBF24" stroke="#92400E" strokeWidth="0.8" />
          {/* Inside shadow */}
          <path d="M3 17 Q3 7 12 7 Q21 7 21 17" fill="none" stroke="#92400E" strokeWidth="0.4" />
          {/* Lettuce + tomatoes */}
          <ellipse cx="8"  cy="11" rx="2" ry="1.2" fill="#22C55E" />
          <ellipse cx="14" cy="10" rx="2.3" ry="1.3" fill="#EF4444" />
          <ellipse cx="11" cy="13" rx="1.8" ry="1" fill="#FBBF24" />
          <ellipse cx="17" cy="13" rx="1.5" ry="0.9" fill="#22C55E" />
          {/* Meat */}
          <ellipse cx="12" cy="15" rx="6" ry="1.2" fill="#7C2D12" />
        </>
      );
    case "pizza":
      return (
        <>
          {/* Slice triangle */}
          <path d="M12 2 L22 20 L2 20 Z" fill="#F59E0B" stroke="#92400E" strokeWidth="0.7" strokeLinejoin="round" />
          {/* Crust band along bottom */}
          <path d="M2 20 L22 20 L21 22 L3 22 Z" fill="#D97706" stroke="#92400E" strokeWidth="0.5" />
          {/* Sauce overlay */}
          <path d="M12 6 L19 19 L5 19 Z" fill="#EF4444" opacity="0.85" />
          {/* Pepperoni */}
          <circle cx="9.5"  cy="14" r="1.4" fill="#7F1D1D" stroke="#FCA5A5" strokeWidth="0.3" />
          <circle cx="14"   cy="13" r="1.2" fill="#7F1D1D" stroke="#FCA5A5" strokeWidth="0.3" />
          <circle cx="12"   cy="17" r="1.0" fill="#7F1D1D" stroke="#FCA5A5" strokeWidth="0.3" />
          <circle cx="15.5" cy="17" r="0.9" fill="#7F1D1D" stroke="#FCA5A5" strokeWidth="0.3" />
          {/* Cheese highlights */}
          <circle cx="11" cy="11" r="0.4" fill="#FEF3C7" />
          <circle cx="13.5" cy="15.5" r="0.3" fill="#FEF3C7" />
        </>
      );
    case "wings":
      return (
        <>
          {/* Drumstick */}
          <path d="M5 18 Q3 16 5 13 Q7 10 11 9 Q15 8 17 6 Q20 4 22 6 Q23 9 21 11 Q19 13 15 14 Q12 15 10 17 Q7 20 5 18 Z" fill="#D97706" stroke="#92400E" strokeWidth="0.7" />
          {/* Bone */}
          <circle cx="4.5" cy="18.5" r="2" fill="#FEF3C7" stroke="#92400E" strokeWidth="0.5" />
          <circle cx="3"   cy="20"  r="1.2" fill="#FEF3C7" stroke="#92400E" strokeWidth="0.5" />
          {/* Buffalo glaze */}
          <path d="M9 12 Q13 10 17 9 Q18 11 16 13 Q12 14 10 14 Z" fill="#DC2626" opacity="0.7" />
        </>
      );
    case "seafood":
      return (
        <>
          {/* Shrimp curl */}
          <path d="M4 14 Q3 8 9 6 Q15 4 19 8 Q22 12 18 16 Q14 19 10 18 Q6 17 6 13" fill="#FB923C" stroke="#9A3412" strokeWidth="0.7" strokeLinejoin="round" />
          {/* Belly stripes */}
          <path d="M6 12 Q8 11 10 12" stroke="#9A3412" strokeWidth="0.5" fill="none" />
          <path d="M9  10 Q11 9  13 10" stroke="#9A3412" strokeWidth="0.5" fill="none" />
          <path d="M13 9  Q15 8  17 9"  stroke="#9A3412" strokeWidth="0.5" fill="none" />
          {/* Tail */}
          <path d="M19 8 L22 5 L22 9 Z" fill="#FB923C" stroke="#9A3412" strokeWidth="0.5" />
          {/* Eye */}
          <circle cx="6" cy="13" r="0.6" fill="#0F172A" />
        </>
      );
    case "bbq":
      return (
        <>
          {/* Skewer line */}
          <line x1="1" y1="13" x2="23" y2="13" stroke="#71717A" strokeWidth="0.6" />
          {/* Meat cubes */}
          <rect x="5"  y="10" width="4" height="6" rx="1" fill="#7C2D12" stroke="#92400E" strokeWidth="0.5" />
          <rect x="10" y="10" width="4" height="6" rx="1" fill="#B45309" stroke="#92400E" strokeWidth="0.5" />
          <rect x="15" y="10" width="4" height="6" rx="1" fill="#7C2D12" stroke="#92400E" strokeWidth="0.5" />
          {/* Flame underneath */}
          <path d="M7 18 Q8 21 9 18 Q10 22 11 18 Q12 21 13 18 Q14 22 15 18 Q16 21 17 18 Q14 23 12 23 Q10 23 7 18 Z" fill="#F59E0B" />
          <path d="M9 19 Q10 22 11 19 Q12 22 13 19 Q14 22 15 19 Q13 23 12 23 Q11 23 9 19 Z" fill="#EF4444" />
        </>
      );
    case "dessert":
      return (
        <>
          {/* Cupcake wrapper */}
          <path d="M5 13 L19 13 L17 22 L7 22 Z" fill="#EC4899" stroke="#831843" strokeWidth="0.7" />
          {/* Wrapper lines */}
          <line x1="9"  y1="13" x2="9"  y2="22" stroke="#831843" strokeWidth="0.4" />
          <line x1="12" y1="13" x2="12" y2="22" stroke="#831843" strokeWidth="0.4" />
          <line x1="15" y1="13" x2="15" y2="22" stroke="#831843" strokeWidth="0.4" />
          {/* Frosting swirl */}
          <path d="M5 13 Q5 7 12 7 Q19 7 19 13 Q19 11 16 10 Q19 9 16 7 Q12 4 8 7 Q5 9 8 10 Q5 11 5 13 Z" fill="#FBCFE8" stroke="#831843" strokeWidth="0.5" />
          {/* Cherry on top */}
          <circle cx="12" cy="5" r="1.4" fill="#DC2626" stroke="#7F1D1D" strokeWidth="0.4" />
          <path d="M12 4 Q13 2 14 2" stroke="#15803D" strokeWidth="0.5" fill="none" />
          {/* Sprinkles */}
          <circle cx="8"  cy="9"  r="0.4" fill="#22D3EE" />
          <circle cx="14" cy="10" r="0.4" fill="#FBBF24" />
          <circle cx="11" cy="11" r="0.4" fill="#22C55E" />
        </>
      );
    case "drink":
      return (
        <>
          {/* Cup */}
          <path d="M5 6 L7 22 L17 22 L19 6 Z" fill="#0EA5E9" fillOpacity="0.6" stroke="#0369A1" strokeWidth="0.8" strokeLinejoin="round" />
          {/* Liquid */}
          <path d="M5.5 8 L6.7 19 L17.3 19 L18.5 8 Z" fill="#22D3EE" />
          {/* Bubbles */}
          <circle cx="9"  cy="12" r="0.8" fill="#FFFFFF" opacity="0.85" />
          <circle cx="13" cy="14" r="0.6" fill="#FFFFFF" opacity="0.8" />
          <circle cx="11" cy="17" r="0.5" fill="#FFFFFF" opacity="0.7" />
          {/* Straw */}
          <rect x="13" y="2" width="2" height="8" fill="#F472B6" rx="0.5" transform="rotate(15 14 6)" />
          {/* Lid line */}
          <line x1="5" y1="6" x2="19" y2="6" stroke="#0369A1" strokeWidth="0.8" />
        </>
      );
    case "hotsauce":
      return (
        <>
          {/* Bottle neck */}
          <rect x="10" y="2" width="4" height="3" fill="#7F1D1D" stroke="#450A0A" strokeWidth="0.5" />
          {/* Bottle body */}
          <path d="M8 5 L8 19 Q8 22 12 22 Q16 22 16 19 L16 5 Z" fill="#DC2626" stroke="#7F1D1D" strokeWidth="0.7" />
          {/* Label */}
          <rect x="8.5" y="10" width="7" height="6" fill="#FEF3C7" stroke="#92400E" strokeWidth="0.4" />
          {/* Flame on label */}
          <path d="M11 11 Q12 14 13 11 Q13.5 13 13 14.5 Q12 15.5 11 14.5 Q10.5 13 11 11 Z" fill="#EF4444" />
          {/* Drip */}
          <path d="M14 5 L14.5 7 L14 8 Z" fill="#7F1D1D" />
          {/* Cap highlight */}
          <rect x="10" y="2" width="4" height="0.8" fill="#FBBF24" />
        </>
      );
    case "chef":
      return (
        <>
          {/* Chef hat pleats */}
          <path d="M5 12 Q5 4 9 4 Q11 2 12 4 Q13 2 15 4 Q19 4 19 12 Z" fill="#FFFFFF" stroke="#A1A1AA" strokeWidth="0.8" strokeLinejoin="round" />
          {/* Pleat divisions */}
          <path d="M8  12 Q8.5  6 9  4"  stroke="#A1A1AA" strokeWidth="0.4" fill="none" />
          <path d="M12 12 Q12 8 12 4"       stroke="#A1A1AA" strokeWidth="0.4" fill="none" />
          <path d="M16 12 Q15.5 6 15 4"  stroke="#A1A1AA" strokeWidth="0.4" fill="none" />
          {/* Hat band */}
          <rect x="4" y="12" width="16" height="3" fill="#F4F4F5" stroke="#A1A1AA" strokeWidth="0.6" rx="0.5" />
          {/* Stripes on band */}
          <line x1="6" y1="13" x2="6" y2="14.5" stroke="#A1A1AA" strokeWidth="0.4" />
          <line x1="18" y1="13" x2="18" y2="14.5" stroke="#A1A1AA" strokeWidth="0.4" />
          {/* Spatula */}
          <rect x="11" y="15" width="2" height="6" fill="#A16207" rx="0.4" />
          <rect x="9.5" y="19" width="5" height="2.5" fill="#71717A" rx="0.5" />
        </>
      );
    case "truck":
      return (
        <>
          {/* Truck body */}
          <rect x="3" y="9"  width="13" height="9" fill="#FBBF24" stroke="#92400E" strokeWidth="0.7" rx="0.5" />
          {/* Cab */}
          <path d="M16 12 L21 12 L21 18 L16 18 Z" fill="#F59E0B" stroke="#92400E" strokeWidth="0.7" />
          <rect x="17" y="13" width="3" height="2.5" fill="#22D3EE" />
          {/* Service window */}
          <rect x="5"  y="11" width="9" height="4" fill="#0F172A" />
          <line x1="5" y1="13" x2="14" y2="13" stroke="#FBBF24" strokeWidth="0.4" />
          {/* Sign */}
          <rect x="6" y="6" width="7" height="2" fill="#EF4444" stroke="#7F1D1D" strokeWidth="0.4" />
          <circle cx="7.5"  cy="7" r="0.3" fill="#FEF3C7" />
          <circle cx="9.5"  cy="7" r="0.3" fill="#FEF3C7" />
          <circle cx="11.5" cy="7" r="0.3" fill="#FEF3C7" />
          {/* Wheels */}
          <circle cx="7"  cy="19.5" r="1.7" fill="#0F172A" stroke="#A1A1AA" strokeWidth="0.4" />
          <circle cx="7"  cy="19.5" r="0.6" fill="#71717A" />
          <circle cx="18" cy="19.5" r="1.7" fill="#0F172A" stroke="#A1A1AA" strokeWidth="0.4" />
          <circle cx="18" cy="19.5" r="0.6" fill="#71717A" />
        </>
      );
    case "street":
      return (
        <>
          {/* Skewer line */}
          <line x1="2" y1="20" x2="22" y2="4" stroke="#71717A" strokeWidth="0.6" />
          {/* Vegetable + meat cubes alternating */}
          <rect x="3"  y="17" width="3.5" height="3.5" rx="0.6" fill="#EF4444" stroke="#7F1D1D" strokeWidth="0.4" transform="rotate(-30 4.75 18.75)" />
          <rect x="7"  y="14" width="3.5" height="3.5" rx="0.6" fill="#22C55E" stroke="#15803D" strokeWidth="0.4" transform="rotate(-30 8.75 15.75)" />
          <rect x="11" y="11" width="3.5" height="3.5" rx="0.6" fill="#FBBF24" stroke="#92400E" strokeWidth="0.4" transform="rotate(-30 12.75 12.75)" />
          <rect x="15" y="8"  width="3.5" height="3.5" rx="0.6" fill="#7C2D12" stroke="#451A03" strokeWidth="0.4" transform="rotate(-30 16.75 9.75)" />
          {/* Steam */}
          <path d="M5 10 Q6 7 5 5"  stroke={color} strokeWidth="0.6" fill="none" strokeLinecap="round" opacity="0.7" />
          <path d="M9 7  Q10 4 9 2" stroke={color} strokeWidth="0.6" fill="none" strokeLinecap="round" opacity="0.7" />
        </>
      );
    default:
      return (
        <circle cx="12" cy="12" r="6" fill={color} />
      );
  }
}

/**
 * Per-tier SVG shape. Most tiers share the classic faceted gem; opal and
 * legendary get unique cuts so high-status badges read instantly.
 */
function renderGemShape(variant, gid, stroke, color) {
  switch (variant) {
    case "prism":
      return (
        <>
          <path
            d="M12 2.2L19.6 8 16.5 21.6H7.5L4.4 8 12 2.2Z"
            fill={`url(#g-prism-${gid})`}
            stroke="#A78BFA"
            strokeWidth={stroke}
            strokeLinejoin="round"
          />
          <path d="M12 2.2L8.2 8h7.6L12 2.2Z" fill="rgba(255,255,255,0.35)" />
          <path d="M4.4 8h3.8L12 21.6 4.4 8Z" fill="rgba(255,255,255,0.10)" />
          <path d="M19.6 8h-3.8L12 21.6 19.6 8Z" fill="rgba(255,255,255,0.18)" />
        </>
      );
    case "ember":
      return (
        <>
          <path
            d="M12 2.2L19.6 8 16.5 21.6H7.5L4.4 8 12 2.2Z"
            fill={`url(#g-ember-${gid})`}
            stroke="#FFD699"
            strokeWidth={stroke}
            strokeLinejoin="round"
          />
          <path d="M12 6.5C10.5 9.5 9 11 10 14c0.4 1 1 1.5 2 1.5s1.6-0.5 2-1.5c1-3-0.5-4.5-2-7.5Z" fill="rgba(255,200,100,0.65)" />
          <path d="M12 9.5c-0.6 1.2-1.4 2.2-0.8 3.6 0.2 0.4 0.5 0.6 0.8 0.6s0.6-0.2 0.8-0.6c0.6-1.4-0.2-2.4-0.8-3.6Z" fill="rgba(255,255,200,0.85)" />
        </>
      );
    case "diamond":
      return (
        <>
          <path
            d="M12 2L21 9.2 12 22 3 9.2 12 2Z"
            fill={`url(#g-diamond-${gid})`}
            stroke="#FFFFFF"
            strokeWidth={stroke}
            strokeLinejoin="round"
          />
          <path d="M3 9.2L12 11.5 21 9.2 12 2Z" fill="rgba(255,255,255,0.55)" />
          <path d="M12 11.5L21 9.2 12 22Z" fill="rgba(125,211,252,0.45)" />
          <path d="M12 11.5L3 9.2 12 22Z" fill="rgba(125,211,252,0.25)" />
          <path d="M9 6L13 9 11 10 9 6Z" fill="rgba(255,255,255,0.9)" />
        </>
      );
    case "opal":
      return (
        <>
          <ellipse
            cx="12" cy="12" rx="8.5" ry="9.5"
            fill={`url(#g-opal-${gid})`}
            stroke="#A78BFA"
            strokeWidth={stroke}
          />
          <circle cx="9"  cy="9"  r="1.3" fill="#22D3EE" opacity="0.85" />
          <circle cx="14" cy="11" r="1.0" fill="#EC4899" opacity="0.85" />
          <circle cx="11" cy="15" r="1.1" fill="#84CC16" opacity="0.8" />
          <circle cx="15" cy="16" r="0.8" fill="#FBBF24" opacity="0.85" />
          <circle cx="8"  cy="14" r="0.7" fill="#A78BFA" opacity="0.85" />
          <path d="M8 6.5C9 5 11 4.5 13 5.5" stroke="rgba(255,255,255,0.6)" strokeWidth="1" fill="none" strokeLinecap="round" />
        </>
      );
    case "legendary":
      return (
        <>
          <path
            d="M12 2L14.5 8 21 9 16 13.5 17.5 20 12 17 6.5 20 8 13.5 3 9 9.5 8 12 2Z"
            fill={`url(#g-legendary-${gid})`}
            stroke="#FFD700"
            strokeWidth={stroke}
            strokeLinejoin="round"
          />
          <path d="M12 2L14.5 8 12 9 9.5 8 12 2Z" fill="rgba(255,255,255,0.6)" />
        </>
      );
    case "classic":
    default:
      return (
        <>
          <path
            d="M12 2.2L19.6 8 16.5 21.6H7.5L4.4 8 12 2.2Z"
            fill={`url(#g-classic-${gid})`}
            stroke={color}
            strokeWidth={stroke}
            strokeLinejoin="round"
          />
          <path d="M12 2.2L8.2 8h7.6L12 2.2Z" fill="rgba(255,255,255,0.35)" />
          <path d="M4.4 8h3.8L12 21.6 4.4 8Z" fill="rgba(0,0,0,0.18)" />
          <path d="M19.6 8h-3.8L12 21.6 19.6 8Z" fill="rgba(255,255,255,0.10)" />
        </>
      );
  }
}
